import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, API_BASE_URL } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Download, Receipt, Send, CheckCircle2,
  FileText, DollarSign, Calendar, User, Mail, MapPin, Percent, History
} from 'lucide-react';
import ReminderHistoryPanel from '@/components/invoices/ReminderHistoryPanel';
import SendReminderModal from '@/components/invoices/SendReminderModal';

type Invoice = {
  id: number;
  invoice_number: string;
  quote_id?: number;
  purchase_order_id?: number;
  percentage: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_city?: string;
  customer_postal_code?: string;
  customer_country?: string;
  status: string;
  issue_date: string;
  due_date?: string;
  paid_date?: string;
  notes?: string;
  payment_terms?: string;
  created_at: string;
};

const InvoiceViewer: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!invoiceId) return;
      try {
        setBusy(true);

        // Charger la facture
        const data = await apiService.getInvoice(Number(invoiceId));
        setInvoice(data);

        // Build direct PDF URL with token for cross-browser compatibility (incl. iOS Chrome)
        const token = useAuthStore.getState().token;
        if (token) {
          setPdfUrl(`${API_BASE_URL}/clients/me/invoices/${invoiceId}/pdf?token=${encodeURIComponent(token)}`);
        }
      } catch (e: any) {
        toast({
          title: 'Erreur',
          description: 'Impossible de charger la facture.',
          variant: 'destructive'
        });
        navigate('/invoices');
      } finally {
        setBusy(false);
      }
    };
    void load();

    // No cleanup needed (direct URL, no blob)
  }, [invoiceId, navigate, toast]);

  const handleDownload = async () => {
    if (!invoice) return;
    try {
      const token = useAuthStore.getState().token;
      if (!token) {
        toast({ title: 'Erreur', description: 'Non authentifié', variant: 'destructive' });
        return;
      }

      // Use direct URL with token for download
      const downloadUrl = `${API_BASE_URL}/clients/me/invoices/${invoice.id}/pdf?token=${encodeURIComponent(token)}`;

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `facture-${invoice.invoice_number || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Téléchargement impossible', variant: 'destructive' });
    }
  };

  const handleSend = async () => {
    if (!invoice) return;
    try {
      await apiService.sendInvoice(invoice.id);
      toast({ title: 'Facture envoyée', description: 'La facture a été marquée comme envoyée.' });
      // Recharger
      const data = await apiService.getInvoice(invoice.id);
      setInvoice(data);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Envoi impossible', variant: 'destructive' });
    }
  };

  const handleMarkPaid = async () => {
    if (!invoice) return;
    try {
      await apiService.markInvoicePaid(invoice.id);
      toast({ title: 'Facture payée', description: 'La facture a été marquée comme payée.' });
      // Recharger
      const data = await apiService.getInvoice(invoice.id);
      setInvoice(data);
    } catch (e: any) {
      toast({ title: 'Erreur', description: 'Mise à jour impossible', variant: 'destructive' });
    }
  };

  const fmtDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
      sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300' },
      paid: { label: 'Payée', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' },
      overdue: { label: 'En retard', color: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300' },
      cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
    };
    return configs[status as keyof typeof configs] || configs.draft;
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Receipt className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Facture introuvable</p>
          <Button onClick={() => navigate('/invoices')} className="mt-4">
            Retour à la liste
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(invoice.status);

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-50 dark:from-slate-950 dark:via-amber-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/invoices')}
            className="hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="h-8 w-px bg-slate-300 dark:bg-slate-700"></div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <Receipt className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {invoice.invoice_number}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              </h1>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Émise le {fmtDate(invoice.issue_date)}
                </span>
                {invoice.due_date && (
                  <>
                    <span>•</span>
                    <span>Échéance: {fmtDate(invoice.due_date)}</span>
                  </>
                )}
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Percent className="h-3 w-3" />
                  {invoice.percentage}% du bon de commande
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {invoice.status === 'draft' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSend}
              className="hover:bg-green-50 hover:text-green-700 hover:border-green-300 dark:hover:bg-green-950/30"
            >
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </Button>
          )}
          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkPaid}
                className="hover:bg-green-50 hover:text-green-700 hover:border-green-300 dark:hover:bg-green-950/30"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Marquer payée
              </Button>
              {invoice.status === 'overdue' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReminderModal(true)}
                  className="hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 dark:hover:bg-orange-950/30"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer relance
                </Button>
              )}
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 dark:hover:bg-amber-950/30"
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex gap-6 p-6">
        {/* Left: Info Panel */}
        <div className="w-96 space-y-4 overflow-auto">
          {/* Client Info */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-amber-600" />
              Informations Client
            </h3>
            <div className="space-y-3 text-sm">
              {invoice.customer_name && (
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Nom</div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{invoice.customer_name}</div>
                </div>
              )}
              {invoice.customer_email && (
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">{invoice.customer_email}</div>
                </div>
              )}
              {invoice.customer_phone && (
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Téléphone</div>
                  <div className="text-slate-700 dark:text-slate-300">{invoice.customer_phone}</div>
                </div>
              )}
              {(invoice.customer_address || invoice.customer_city) && (
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Adresse
                  </div>
                  <div className="text-slate-700 dark:text-slate-300">
                    {invoice.customer_address && <div>{invoice.customer_address}</div>}
                    {(invoice.customer_postal_code || invoice.customer_city) && (
                      <div>{[invoice.customer_postal_code, invoice.customer_city].filter(Boolean).join(' ')}</div>
                    )}
                    {invoice.customer_country && <div>{invoice.customer_country}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Amounts */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-amber-600" />
              Montants
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Sous-total HT</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{invoice.subtotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">TVA</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{invoice.tax_amount.toFixed(2)} €</span>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex justify-between">
                <span className="font-bold text-slate-900 dark:text-slate-100">Total TTC</span>
                <span className="text-xl font-bold text-amber-600">{invoice.total.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Bon de commande Source */}
          {invoice.purchase_order_id && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                Bon de commande
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/achat/orders/${invoice.purchase_order_id}`)}
                className="w-full"
              >
                Voir le bon de commande #{invoice.purchase_order_id}
              </Button>
            </div>
          )}

          {/* Payment Terms */}
          {invoice.payment_terms && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-3">Conditions de paiement</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300">{invoice.payment_terms}</p>
            </div>
          )}

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-3">Notes</h3>
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          {/* Reminder History */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-amber-600" />
              Historique des relances
            </h3>
            <ReminderHistoryPanel invoiceId={invoice.id} />
          </div>
        </div>

        {/* Right: PDF Viewer */}
        <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full"
              title="Invoice PDF"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Receipt className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">Chargement du PDF...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Reminder Modal */}
      {invoice && (
        <SendReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          invoice={{
            ...invoice,
            days_overdue: 0,
            penalty_amount: 0
          }}
          onSuccess={async () => {
            const data = await apiService.getInvoice(invoice.id);
            setInvoice(data);
          }}
        />
      )}
    </div>
  );
};

export default InvoiceViewer;
